if (!customElements.get('custom-size-calculator')) {
  customElements.define(
    'custom-size-calculator',
    class CustomSizeCalculator extends HTMLElement {
      connectedCallback() {
        this._sectionId  = this.dataset.sectionId;
        this._variantId  = parseInt(this.dataset.variantId, 10);
        this._optionName = this.dataset.optionName || 'Size';
        this._minW       = parseInt(this.dataset.minWidth,  10);
        this._maxW       = parseInt(this.dataset.maxWidth,  10);
        this._minH       = parseInt(this.dataset.minHeight, 10);
        this._maxH       = parseInt(this.dataset.maxHeight, 10);
        this._qty        = 1;

        this._overlay  = this.querySelector('.csc-overlay');
        this._modal    = this.querySelector('.csc-modal');
        this._closeBtn = this.querySelector('.csc-modal__close');
        this._widthIn  = this.querySelector(`#csc-w-${this._sectionId}`);
        this._heightIn = this.querySelector(`#csc-h-${this._sectionId}`);
        // Only present when the product has a Thickness option.
        this._thickIn  = this.querySelector(`#csc-t-${this._sectionId}`);
        this._thickLabel = this.dataset.thicknessLabel || 'Thickness';
        this._wErr     = this.querySelector(`#csc-werr-${this._sectionId}`);
        this._hErr     = this.querySelector(`#csc-herr-${this._sectionId}`);
        this._qtyVal   = this.querySelector('.csc-modal__qty-val');
        this._atcBtn   = this.querySelector('.csc-modal__atc');
        this._cartErr  = this.querySelector(`#csc-cart-err-${this._sectionId}`);

        this._customKeyword = (this.dataset.customKeyword || 'Custom').toLowerCase();

        this._trigger  = this.querySelector('.csc-trigger');
        this._hideCustomOptions();
        this._bindTrigger();
        this._bindModal();
        this._bindQty();
        this._bindAtc();
        this._reopenAfterEnquiry();
      }

      // ─── Enquiry mode ─────────────────────────────────────────────────────

      // The enquiry form is a normal Shopify contact form, so submitting it
      // reloads the page — and the modal it was inside is closed again. If the
      // reloaded page carries a success or error message, reopen the modal so
      // the customer sees the outcome rather than an apparently ignored form.
      _reopenAfterEnquiry() {
        if (!this.querySelector('.csc-enquiry__success, .csc-enquiry__error')) return;
        this._open();
        this.querySelector('.csc-enquiry__success, .csc-enquiry__error')?.focus();
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this._onKeyDown);
      }

      // ─── Trigger button ───────────────────────────────────────────────────

      _bindTrigger() {
        this._trigger?.addEventListener('click', () => this._open());
      }

      // ─── Hide custom option from picker ──────────────────────────────────

      _hideCustomOptions() {
        const productInfo =
          this.closest('product-info') ||
          document.querySelector(`product-info[data-section="${this._sectionId}"]`);
        if (!productInfo) return;

        // Radio / swatch labels
        productInfo.querySelectorAll('.product-form__input label').forEach((label) => {
          const text = label.textContent.trim().toLowerCase();
          if (text === this._customKeyword || text.startsWith(this._customKeyword + ' ')) {
            label.style.display = 'none';
            const input = label.htmlFor
              ? productInfo.querySelector(`#${CSS.escape(label.htmlFor)}`)
              : label.previousElementSibling;
            if (input) input.style.display = 'none';
          }
        });

        // Dropdown <option> elements
        productInfo.querySelectorAll('.product-form__input option').forEach((opt) => {
          const text = opt.value.trim().toLowerCase();
          if (text === this._customKeyword || text.startsWith(this._customKeyword + ' ')) {
            opt.hidden   = true;
            opt.disabled = true;
          }
        });
      }

      // ─── Modal open / close ───────────────────────────────────────────────

      _open() {
        // Teleport overlay to <body> so no ancestor clips it
        document.body.appendChild(this._overlay);
        this._overlay.hidden = false;
        document.body.style.overflow = 'hidden';
        this._onKeyDown = (e) => { if (e.key === 'Escape') this._close(); };
        document.addEventListener('keydown', this._onKeyDown);
        if (typeof trapFocus !== 'undefined') trapFocus(this._modal);
        this._widthIn?.focus();
      }

      _close() {
        this._overlay.hidden = true;
        // Return overlay to component so it stays with this element
        this.appendChild(this._overlay);
        document.body.style.overflow = '';
        document.removeEventListener('keydown', this._onKeyDown);
        if (typeof removeTrapFocus !== 'undefined') removeTrapFocus(this._trigger);
        this._trigger?.focus();
        this._setAtcState('idle');
        this._hideError(this._wErr);
        this._hideError(this._hErr);
        this._hideCartError();
      }

      _bindModal() {
        this._closeBtn?.addEventListener('click', () => this._close());
        this._overlay?.addEventListener('click', (e) => {
          if (e.target === this._overlay) this._close();
        });
      }

      // ─── Quantity stepper ─────────────────────────────────────────────────

      _bindQty() {
        this.querySelectorAll('[data-csc-qty]').forEach((btn) => {
          btn.addEventListener('click', () => {
            const delta = btn.dataset.cscQty === '+' ? 1 : -1;
            this._qty = Math.max(1, this._qty + delta);
            if (this._qtyVal) this._qtyVal.textContent = this._qty;
          });
        });
      }

      // ─── Add to cart ──────────────────────────────────────────────────────

      _bindAtc() {
        this._atcBtn?.addEventListener('click', () => this._addToCart());
      }

      _addToCart() {
        const width  = parseFloat(this._widthIn?.value);
        const height = parseFloat(this._heightIn?.value);
        let valid = true;

        this._hideError(this._wErr);
        this._hideError(this._hErr);
        this._hideCartError();

        if (!this._widthIn?.value || isNaN(width) || width < this._minW || width > this._maxW) {
          this._showError(this._wErr, `Width must be between ${this._minW}–${this._maxW} mm`);
          valid = false;
        }
        if (!this._heightIn?.value || isNaN(height) || height < this._minH || height > this._maxH) {
          this._showError(this._hErr, `Height must be between ${this._minH}–${this._maxH} mm`);
          valid = false;
        }
        if (!valid) {
          (this._widthIn?.value ? this._heightIn : this._widthIn)?.focus();
          return;
        }

        this._setAtcState('loading');

        // Thickness rides along as a line item property, like width and height.
        // It deliberately does not switch variant: the custom-size variant is
        // one specific variant matched by keyword, so selecting a different one
        // would take the order off it.
        const properties = {
          'Width (mm)':  Math.round(width),
          'Height (mm)': Math.round(height),
        };
        if (this._thickIn?.value) properties[this._thickLabel] = this._thickIn.value;

        // Same locale-rooted .js endpoint every other add path uses, so all of
        // them land in one cart.
        const addUrl = `${window.Shopify?.routes?.root || '/'}cart/add.js`.replace('//cart', '/cart');

        fetch(addUrl, {
          method:  'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id:         this._variantId,
            quantity:   this._qty,
            properties: properties,
            sections:     ['cart-drawer', 'cart-icon-bubble'],
            sections_url: window.location.pathname,
          }),
        })
          .then((r) => r.json())
          .then((response) => {
            if (response.status) {
              this._setAtcState('idle');
              this._showCartError(response.description || response.message || 'Could not add to cart.');
              return;
            }
            this._setAtcState('added');
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer) {
              cartDrawer.renderContents(response);
            } else if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source:    'custom-size-calculator',
                cartData:  response,
              });
            }
            setTimeout(() => this._close(), 700);
          })
          .catch(() => {
            this._setAtcState('idle');
            this._showCartError('Something went wrong. Please try again.');
          });
      }

      // ─── Helpers ─────────────────────────────────────────────────────────

      _setAtcState(state) {
        if (!this._atcBtn) return;
        const label = this._atcBtn.querySelector('.csc-modal__atc-label');
        if (state === 'loading') {
          this._atcBtn.disabled = true;
          if (label) label.textContent = 'Adding…';
        } else if (state === 'added') {
          this._atcBtn.disabled = true;
          if (label) label.textContent = 'Added!';
        } else {
          this._atcBtn.disabled = false;
          if (label) label.textContent = 'ADD TO CART';
        }
      }

      _showError(el, msg) {
        if (!el) return;
        el.textContent = msg;
        el.hidden = false;
        el.previousElementSibling?.classList.add('is-invalid');
      }

      _hideError(el) {
        if (!el) return;
        el.textContent = '';
        el.hidden = true;
        el.previousElementSibling?.classList.remove('is-invalid');
      }

      _showCartError(msg) {
        if (!this._cartErr) return;
        this._cartErr.textContent = msg;
        this._cartErr.hidden = false;
      }

      _hideCartError() {
        if (!this._cartErr) return;
        this._cartErr.textContent = '';
        this._cartErr.hidden = true;
      }
    }
  );
}
